import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Quote, ArrowRight } from 'lucide-react';
import API_BASE from '../services/apiBase';
import { resolvePreviewUrl } from '../utils/images';
import { StarDisplay } from './StarRating';

function formatReviewDate(d) {
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  return x.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

export default function HomeReviews() {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/reviews/public?minRating=5&limit=9`);
        const json = await res.json().catch(() => ({}));
        if (!cancelled && res.ok) {
          setReviews(Array.isArray(json.data) ? json.data : []);
          setSummary(json.summary || null);
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!reviews.length) return null;

  return (
    <section className="jet-section jet-section--muted jet-reviews-section" aria-labelledby="home-reviews-title">
      <div className="jet-container">
        <div className="jet-reviews-head">
          <div>
            <p className="jet-reviews-eyebrow">Customer stories</p>
            <h2 id="home-reviews-title" className="jet-section__title">
              What our customers say
            </h2>
            {summary?.fiveStar > 0 && (
              <p className="jet-reviews-summary">
                <StarDisplay rating={5} size={18} />
                <span>
                  {summary.fiveStar} five-star review{summary.fiveStar !== 1 ? 's' : ''}
                  {summary.count > summary.fiveStar ? ` · ${summary.count} total` : ''}
                </span>
              </p>
            )}
          </div>
          <Link to="/review" className="jet-btn jet-btn-secondary jet-reviews-cta">
            Leave a review <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="jet-reviews-grid">
          {reviews.map((r, idx) => {
            const photoSrc = r.photoUrl
              ? resolvePreviewUrl(r.photoUrl, API_BASE, r._id)
              : '';
            return (
              <motion.article
                key={r._id}
                className="jet-review-card"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
              >
                <Quote className="jet-review-card__quote" aria-hidden />
                <StarDisplay rating={r.rating} size={16} />
                <p className="jet-review-card__text">&ldquo;{r.comment}&rdquo;</p>
                {photoSrc ? (
                  <div className="jet-review-card__photo">
                    <img src={photoSrc} alt={`Review by ${r.name}`} loading="lazy" />
                  </div>
                ) : null}
                <footer className="jet-review-card__foot">
                  <div>
                    <strong>{r.name}</strong>
                    {r.hblNo ? <span className="jet-review-card__hbl">HBL: {r.hblNo}</span> : null}
                  </div>
                  {r.createdAt ? <span>{formatReviewDate(r.createdAt)}</span> : null}
                </footer>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
